import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Rocket, Trash2, Calendar, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import type { DistributionCampaign, DistributionChannelKey } from "@shared/schema";
import { AIDraftButton } from "@/components/ai-draft-button";

const CHANNELS: { key: DistributionChannelKey; label: string; group: string }[] = [
  { key: "press_release", label: "Press Release", group: "Press" },
  { key: "fan_newsletter", label: "Fan Newsletter", group: "Email" },
  { key: "buyer_newsletter", label: "Buyer Newsletter", group: "Email" },
  { key: "social_facebook", label: "Facebook", group: "Social" },
  { key: "social_instagram", label: "Instagram", group: "Social" },
  { key: "social_twitter", label: "Twitter / X", group: "Social" },
  { key: "social_linkedin", label: "LinkedIn", group: "Social" },
  { key: "social_tiktok", label: "TikTok", group: "Social" },
  { key: "buyer_one_sheet", label: "Buyer One-Sheet", group: "Sales" },
  { key: "booking_page_refresh", label: "Booking Page Refresh", group: "Web" },
  { key: "youtube", label: "YouTube", group: "Social" },
  { key: "listings", label: "Listings (Eventful, etc.)", group: "Web" },
];

const formSchema = z.object({
  title: z.string().min(1, "Title required"),
  talentId: z.string().min(1, "Talent required"),
  description: z.string().optional(),
  eventDates: z.string().optional(),
  markets: z.string().optional(),
  channels: z.array(z.string()).default([]),
});

type FormValues = z.infer<typeof formSchema>;

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  draft: "secondary",
  scheduled: "outline",
  launched: "default",
  complete: "default",
  archived: "outline",
};

export default function MarketingDistributionPage() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: items, isLoading } = useQuery<DistributionCampaign[]>({
    queryKey: ["/api/marketing/distribution"],
  });

  const { data: talent } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/talent"],
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", talentId: "", channels: [] },
  });

  const createMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const channelsConfig: Record<string, any> = {};
      values.channels.forEach((k) => {
        channelsConfig[k] = { enabled: true, status: "pending" };
      });
      const payload = {
        title: values.title,
        talentId: values.talentId,
        description: values.description,
        eventDates: values.eventDates ? values.eventDates.split(",").map((s) => s.trim()).filter(Boolean) : [],
        markets: values.markets ? values.markets.split(",").map((s) => s.trim()).filter(Boolean) : [],
        channelsConfig,
        assetRefs: {},
        status: "draft",
      };
      const res = await apiRequest("POST", "/api/marketing/distribution", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/distribution"] });
      toast({ title: "Campaign created" });
      setCreateOpen(false);
      form.reset({ title: "", talentId: "", channels: [] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to create", description: err?.message, variant: "destructive" });
    },
  });

  const launchMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/marketing/distribution/${id}/launch`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/distribution"] });
      toast({ title: "Campaign launched" });
    },
    onError: (err: any) => {
      toast({ title: "Launch failed", description: err?.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/marketing/distribution/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/distribution"] });
      toast({ title: "Campaign deleted" });
    },
  });

  const channelGroups = CHANNELS.reduce<Record<string, typeof CHANNELS>>((acc, c) => {
    (acc[c.group] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6" data-testid="page-marketing-distribution">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Distribution Hub</h1>
          <p className="text-sm text-muted-foreground">Push talent and shows across every channel from one place.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-campaign">
              <Plus className="w-4 h-4 mr-2" /> New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Distribution Campaign</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="title" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl><Input {...field} placeholder="Summer Tour Push" data-testid="input-title" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="talentId" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Talent</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger data-testid="select-talent"><SelectValue placeholder="Select..." /></SelectTrigger></FormControl>
                        <SelectContent>
                          {talent?.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Description / Pitch angle</FormLabel>
                      <AIDraftButton
                        kind="marketing"
                        context={`Current pitch:\n${field.value || "(empty)"}`}
                        onDraft={(text) => field.onChange(text)}
                      />
                    </div>
                    <FormControl><Textarea rows={3} {...field} data-testid="input-description" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="eventDates" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Show dates (comma-separated)</FormLabel>
                      <FormControl><Input {...field} placeholder="2026-06-15, 2026-06-22" data-testid="input-show-dates" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="markets" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Markets (comma-separated)</FormLabel>
                      <FormControl><Input {...field} placeholder="Nashville, Atlanta, Dallas" data-testid="input-markets" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="channels" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channels</FormLabel>
                    <div className="space-y-3 border rounded-md p-3">
                      {Object.entries(channelGroups).map(([group, chans]) => (
                        <div key={group}>
                          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">{group}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {chans.map((c) => {
                              const checked = field.value?.includes(c.key);
                              return (
                                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(v) => {
                                      const next = v
                                        ? [...(field.value || []), c.key]
                                        : (field.value || []).filter((k) => k !== c.key);
                                      field.onChange(next);
                                    }}
                                    data-testid={`checkbox-channel-${c.key}`}
                                  />
                                  {c.label}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />

                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-save-campaign">
                    {createMutation.isPending ? "Saving..." : "Save Draft"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : !items || items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="text-empty-state">
            No campaigns yet. Create your first to coordinate a multi-channel push.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => {
            const enabledChannels = Object.entries((c.channelsConfig as any) || {})
              .filter(([, cfg]: [string, any]) => cfg?.enabled)
              .map(([k]) => CHANNELS.find((ch) => ch.key === k)?.label || k);
            return (
              <Card key={c.id} data-testid={`card-campaign-${c.id}`}>
                <CardContent className="p-4 flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid={`text-campaign-title-${c.id}`}>{c.title}</span>
                      <Badge variant={statusVariant[c.status] || "secondary"}>{c.status}</Badge>
                      {c.utmCampaign && <Badge variant="outline" className="font-mono text-xs">{c.utmCampaign}</Badge>}
                    </div>
                    {c.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>}
                    <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      {c.eventDates && c.eventDates.length > 0 && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{c.eventDates.length} date{c.eventDates.length > 1 ? "s" : ""}</span>
                      )}
                      {c.markets && c.markets.length > 0 && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.markets.join(", ")}</span>
                      )}
                      {c.launchedAt && <span>Launched {format(new Date(c.launchedAt), "MMM d, yyyy")}</span>}
                    </div>
                    {enabledChannels.length > 0 && (
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {enabledChannels.map((label) => (
                          <Badge key={label} variant="secondary" className="text-xs">{label}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {c.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => launchMutation.mutate(c.id)}
                        disabled={launchMutation.isPending}
                        data-testid={`button-launch-${c.id}`}
                      >
                        <Rocket className="w-4 h-4 mr-1" /> Launch
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(c.id)} data-testid={`button-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
